import { CSSProperties, ReactNode } from "react"
import { Box, Collapse, LinearProgress } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { cellForWeek, cellKeyFor, WeekStore } from "../../useWeekMenuStore"
import type { DayFillProgress } from "../../weekMenuSelectors"
import { WeekCategoryRows, WeekMenuDayState, WEEK_DAY_COUNT } from "../../weekMenuTypes"
import { ClosedDayPanel } from "./ClosedDayPanel"
import { DishDayCell } from "../cell/DishDayCell"
import { DishRowLabel } from "./DishRowLabel"
import { ThemeDayPanel } from "./ThemeDayPanel"
import { WeekCategoryRow } from "./WeekCategoryRow"
import { WeekDayHeader } from "./WeekDayHeader"

const useStyles = makeStyles()((theme) => ({
    // width 0 plus minWidth 100% stops the grid's min-content width from stretching the whole page.
    scrollArea: {
        width: 0,
        minWidth: "100%",
        overflowX: "auto",
        border: `1px solid ${theme.palette.tailwind.gray[200]}`,
        borderRadius: ".625rem",
        // The table background lives on the scroll container, so overscroll shows it, not white.
        background: theme.palette.tailwind.gray[50],
    },
    table: {
        display: "grid",
        gridTemplateColumns: "15rem repeat(7, minmax(14.5rem, 1fr))",
    },
    // Explicit row and column placement lets the theme and closed panels span all dish rows.
    slot: {
        gridRow: "var(--wm-grid-row)",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        "& > *": {
            flexGrow: 1,
        },
        // Forcing 100% before Collapse finishes entering makes it measure zero height and jump open.
        "& .MuiCollapse-entered .MuiCollapse-wrapper": {
            height: "100%",
        },
        "& .MuiCollapse-entered .MuiCollapse-wrapperInner": {
            height: "100%",
        },
    },
    // On collapse the wrapper keeps the stretched height, so every cell of the row animates from the
    // same row height instead of its own shorter content height and they shrink in step.
    slotCollapsing: {
        "& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner": {
            height: "100%",
        },
    },
    stickyColumn: {
        position: "sticky",
        left: 0,
        zIndex: 5,
        // Opaque, so the horizontally scrolled cells never show through while a Collapse animates
        // and leaves the slot taller than the label inside it.
        background: theme.palette.background.paper,
        borderRight: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    fullRow: {
        gridColumn: "1 / -1",
    },
    // The day panels overlay their column without sizing the grid, or collapsing a category would
    // stretch the table to the panel's full height.
    panelSlot: {
        position: "relative",
        minHeight: 0,
    },
    panelOverlay: {
        position: "absolute",
        inset: 0,
        // Above the cells' corner tools (zIndex 2), which would otherwise paint and hover through
        // the overlay's own background layer, e.g. in the scrollbar gutter.
        zIndex: 3,
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
        // Grow to fill a short column, but never shrink: a shrunken panel would fit the overlay
        // exactly, its content would bleed out and the overlay would never scroll.
        "& > *": {
            flexGrow: 1,
            flexShrink: 0,
        },
    },
    // The scroll container carries the panel's background too, so the scrollbar gutter and any
    // scroll rounding gaps show the panel color instead of white.
    panelOverlayTheme: {
        background: theme.palette.tailwind.orange[50],
    },
    panelOverlayClosed: {
        background: theme.palette.tailwind.slate[50],
    },
    corner: {
        background: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    loadingBar: {
        gridColumn: "1 / -1",
        gridRow: "1",
        alignSelf: "end",
        zIndex: 6,
        pointerEvents: "none",
    },
    column1: { gridColumn: "1" },
    column2: { gridColumn: "2" },
    column3: { gridColumn: "3" },
    column4: { gridColumn: "4" },
    column5: { gridColumn: "5" },
    column6: { gridColumn: "6" },
    column7: { gridColumn: "7" },
    column8: { gridColumn: "8" },
}))

type Props = Readonly<{
    categories: ReadonlyArray<WeekCategoryRows>
    week: WeekStore
    days: ReadonlyArray<WeekMenuDayState>
    /** Shows the thin progress line under the day headers (AI generation in progress). */
    isLoading: boolean
    /** Cells the AI review banner marks as freshly generated. */
    aiFilledKeys: ReadonlySet<string>
    dayNames: ReadonlyArray<string>
    dayDates: ReadonlyArray<string>
    /** Per-day filled/total cell counts shown as the day header badge. */
    dayFillProgress: ReadonlyArray<DayFillProgress>
    /** Per-row filled/total cell counts, keyed by row key, shown next to the dish name. */
    rowFillProgress: ReadonlyMap<string, DayFillProgress>
    collapsedCategories: ReadonlySet<number>
    onToggleCategory: (categoryIndex: number) => void
    onMakeTheme: (dayIndex: number) => void
    onRenameTheme: (dayIndex: number) => void
    onRemoveTheme: (dayIndex: number) => void
    onCloseDay: (dayIndex: number) => void
}>

export function WeekMenuTable({
    categories,
    week,
    days,
    isLoading,
    aiFilledKeys,
    dayNames,
    dayDates,
    dayFillProgress,
    rowFillProgress,
    collapsedCategories,
    onToggleCategory,
    onMakeTheme,
    onRenameTheme,
    onRemoveTheme,
    onCloseDay,
}: Props) {
    const { classes, cx } = useStyles()

    const dayColumnClasses = [
        classes.column2,
        classes.column3,
        classes.column4,
        classes.column5,
        classes.column6,
        classes.column7,
        classes.column8,
    ]

    // React's CSSProperties has no typing for CSS custom properties, hence the narrow cast.
    const rowStyle = (gridRow: string) => ({ "--wm-grid-row": gridRow }) as CSSProperties

    const children: ReactNode[] = []
    let rowIndex = 1

    children.push(
        <Box
            key="corner"
            className={cx(classes.slot, classes.column1, classes.stickyColumn, classes.corner)}
            style={rowStyle("1")}
        />,
    )
    days.forEach((dayState, dayIndex) => {
        children.push(
            <Box
                key={`day-head-${dayIndex}`}
                className={cx(classes.slot, dayColumnClasses[dayIndex])}
                style={rowStyle("1")}
            >
                <WeekDayHeader
                    dayIndex={dayIndex}
                    dayName={dayNames[dayIndex]}
                    dayDate={dayDates[dayIndex]}
                    dayState={dayState}
                    filledCount={dayFillProgress[dayIndex].filledCount}
                    totalCount={dayFillProgress[dayIndex].totalCount}
                    onMakeTheme={() => onMakeTheme(dayIndex)}
                    onRenameTheme={() => onRenameTheme(dayIndex)}
                    onRemoveTheme={() => onRemoveTheme(dayIndex)}
                    onCloseDay={() => onCloseDay(dayIndex)}
                />
            </Box>,
        )
    })

    if (isLoading) {
        children.push(
            <Box key="loading-bar" className={classes.loadingBar}>
                <LinearProgress />
            </Box>,
        )
    }

    categories.forEach((category, categoryIndex) => {
        rowIndex += 1
        const isCollapsed = collapsedCategories.has(categoryIndex)
        children.push(
            <Box
                key={`category-${categoryIndex}`}
                className={cx(classes.slot, classes.fullRow)}
                style={rowStyle(String(rowIndex))}
            >
                <WeekCategoryRow
                    name={category.name}
                    dishNames={category.dishes.map((row) => row.name)}
                    isCollapsed={isCollapsed}
                    onToggle={() => onToggleCategory(categoryIndex)}
                />
            </Box>,
        )
        category.dishes.forEach((row) => {
            rowIndex += 1
            const rowProgress = rowFillProgress.get(row.key)
            children.push(
                <Box
                    key={`label-${row.key}`}
                    className={cx(
                        classes.slot,
                        classes.column1,
                        classes.stickyColumn,
                        isCollapsed && classes.slotCollapsing,
                    )}
                    style={rowStyle(String(rowIndex))}
                >
                    <Collapse in={!isCollapsed}>
                        <DishRowLabel
                            row={row}
                            filledCount={rowProgress?.filledCount ?? 0}
                            totalCount={rowProgress?.totalCount ?? row.availableDayIndexes.length}
                        />
                    </Collapse>
                </Box>,
            )
            days.forEach((dayState, dayIndex) => {
                children.push(
                    <Box
                        key={`cell-${row.key}-${dayIndex}`}
                        className={cx(classes.slot, dayColumnClasses[dayIndex], isCollapsed && classes.slotCollapsing)}
                        style={rowStyle(String(rowIndex))}
                    >
                        <Collapse in={!isCollapsed}>
                            <DishDayCell
                                row={row}
                                dayIndex={dayIndex}
                                cell={cellForWeek(week, row, dayIndex)}
                                isAiFresh={aiFilledKeys.has(cellKeyFor(row.key, dayIndex))}
                            />
                        </Collapse>
                    </Box>,
                )
            })
        })
    })

    const panelGridRow = `2 / ${rowIndex + 1}`
    const hasBodyRows = rowIndex > 1

    if (hasBodyRows) {
        Array.from({ length: WEEK_DAY_COUNT }, (_, dayIndex) => dayIndex).forEach((dayIndex) => {
            const dayState = days[dayIndex]
            if (!dayState.theme && !dayState.isClosed) return
            children.push(
                <Box
                    key={`panel-${dayIndex}`}
                    className={cx(classes.slot, dayColumnClasses[dayIndex], classes.panelSlot)}
                    style={rowStyle(panelGridRow)}
                >
                    <Box
                        className={cx(
                            classes.panelOverlay,
                            dayState.theme ? classes.panelOverlayTheme : classes.panelOverlayClosed,
                        )}
                    >
                        {dayState.theme ? (
                            <ThemeDayPanel
                                dayIndex={dayIndex}
                                theme={dayState.theme}
                                onRename={() => onRenameTheme(dayIndex)}
                                onRevert={() => onRemoveTheme(dayIndex)}
                            />
                        ) : (
                            <ClosedDayPanel dayIndex={dayIndex} />
                        )}
                    </Box>
                </Box>,
            )
        })
    }

    return (
        <Box className={classes.scrollArea}>
            <Box className={classes.table}>{children}</Box>
        </Box>
    )
}
